import type { BeforeSendFn, CapturedNetworkRequest } from 'posthog-js';

const REDACTED = '[redacted]';

/** Attribute (and legacy rrweb class) every sensitive element on the buyer surface carries. */
export const MASK_TEXT_SELECTOR = '[data-ph-mask], .ph-mask';
function encodedRouteWord(word: string): string {
  return Array.from(word, (letter) => {
    const encoded = new Set([letter.toLowerCase().charCodeAt(0), letter.toUpperCase().charCodeAt(0)]);
    return `(?:${letter}|${Array.from(encoded, (code) => `%(?:25)?${code.toString(16)}`).join('|')})`;
  }).join('');
}

const secretNames = ['confirmAcceptance', 'confirmationId', 'paymentProof', 'quoteToken', 'magicLinkToken', 'x-quote-confirmation', 'x_quote_confirmation'].map(encodedRouteWord).join('|');
const sensitiveKey = new RegExp(`^(?:${secretNames})$`, 'i');
const namedSecret = new RegExp(String.raw`((?:${secretNames})(?:\\?["']|\\u0022|\\u0027|%(?:25)?22|%(?:25)?27)?(?:\s|%(?:25)?20)*(?:=|:|%(?:25)?3d|%(?:25)?3a)(?:\s|%(?:25)?20)*(?:\\?["']|\\u0022|\\u0027|%(?:25)?22|%(?:25)?27)?)[^&\s"'<>;,}]+`, 'gi');

const separator = String.raw`(?:/|\\{1,4}/|\\{1,4}u002f|%(?:25)?2f)`;
const capabilityPath = new RegExp(`(${separator}(?:${encodedRouteWord('q')}|(?:${encodedRouteWord('api')}${separator})?${encodedRouteWord('customer')}${separator}${encodedRouteWord('quote')}|${encodedRouteWord('accept-confirmation')})${separator})[^/?#\\s"'<>;&]+`, 'gi');

export function isPaymentPage(path = window.location.pathname): boolean {
  const segment = path.split('/')[1];
  try {
    return Boolean(segment && decodeURIComponent(segment).toLowerCase() === 'q');
  } catch {
    return false;
  }
}

export function redactPaymentText(value: string): string {
  return value
    .replace(capabilityPath, `$1${REDACTED}`)
    .replace(namedSecret, `$1${REDACTED}`);
}

// A DOM snapshot carries addresses as attributes, not as text, so masking never touches them: a
// `mailto:` href hands over the rep's address in full. Only the replay payload is rewritten this
// way — an ordinary event's email property is a deliberate, reviewed field and stays intact.
const mailtoAddress = /(mailto:)[^"'\s<>)\\]+/gi;

export function redactSnapshotText(value: string): string {
  return redactPaymentText(value).replace(mailtoAddress, `$1${REDACTED}`);
}

function redact(value: unknown, redactText: (text: string) => string, seen: WeakSet<object>): unknown {
  if (typeof value === 'string') return redactText(value);
  if (!value || typeof value !== 'object') return value;
  if (value instanceof Date) return value;
  if (seen.has(value)) return '[circular]';
  seen.add(value);
  if (Array.isArray(value)) return value.map((item) => redact(item, redactText, seen));
  return Object.fromEntries(Object.entries(value).map(([key, item]) => [
    redactText(key),
    sensitiveKey.test(key) ? REDACTED : redact(item, redactText, seen),
  ]));
}

export function redactPaymentTelemetry(value: unknown, seen = new WeakSet<object>()): unknown {
  return redact(value, redactPaymentText, seen);
}

export function redactSnapshotTelemetry(value: unknown, seen = new WeakSet<object>()): unknown {
  return redact(value, redactSnapshotText, seen);
}

// rrweb fixes its masking options when the recorder starts and keeps them across client-side
// navigation, so which surface a node belongs to is decided here, from the route it is serialized
// on. Staff routes are recorded as the rep sees them; the buyer surface keeps rrweb's masking.
export function maskRecordedText(text: string): string {
  return isPaymentPage() ? text.replace(/\S/g, '*') : text;
}

const passwordInput = 'input[type="password"], [data-rr-is-password]';

export function maskRecordedInput(value: string, element?: HTMLElement): string {
  return !element || isPaymentPage() || element.matches(passwordInput) ? '*'.repeat(value.length) : value;
}

/**
 * Attributes that read back as on-screen text. rrweb masks text nodes, never attributes, so an
 * `aria-label` or `title` built from quote data survives masking and lands in the snapshot verbatim.
 * Several of them are a11y contracts with their own tests, so they are masked in the recorder
 * rather than stripped from the DOM.
 */
const maskedTextAttributes = new Set(['aria-label', 'aria-description', 'aria-valuetext', 'alt', 'placeholder', 'title']);
const linkAttributes = new Set(['href', 'src', 'action', 'formaction']);

/**
 * Applied by the recorder to every serialized attribute. Text-bearing attributes are asterisked
 * inside a masked subtree on the buyer surface — the same boundary that governs the visible text —
 * and link attributes are redacted everywhere, so the buyer's capability token and a rep's address
 * never reach the payload even if replay compression is ever turned back on.
 */
export function maskRecordedAttribute(name: string, value: string, element?: Element): string {
  const attribute = name.toLowerCase();
  if (linkAttributes.has(attribute)) return redactSnapshotText(value);
  if (!maskedTextAttributes.has(attribute) || !element || !isPaymentPage()) return value;
  return element.closest(MASK_TEXT_SELECTOR) === null ? value : value.replace(/\S/g, '*');
}

// Request/response bodies can contain proofs without a recognizable URL. Recording neither is
// safer than trying to infer their schema, including when remote replay settings change.
export function maskPaymentNetworkRequest(request: CapturedNetworkRequest): CapturedNetworkRequest | null {
  if (redactPaymentText(request.name) !== request.name || isPaymentPage()) return null;
  return {
    ...request,
    name: redactPaymentText(request.name),
    requestHeaders: undefined,
    responseHeaders: undefined,
    requestBody: undefined,
    responseBody: undefined,
  };
}

// Replay and autocapture stay on for the buyer surface; what leaves the page is masked in the
// recorder (see lib/posthog.ts) and every property still passes through the redaction here, so a
// capability token can never ride out on an event. Snapshots get the stricter pass: they are the
// only payload carrying raw DOM attributes, which no text masking can reach. They only arrive here
// as readable JSON while replay compression is off (buyer surface), and the extra pass costs
// nothing on a compressed payload.
// Autocapture gets the same pass: its element chain carries each ancestor's `attr__href`, so a
// click anywhere near the rep's mailto link would otherwise hand over the address.
const attributeCarryingEvents = new Set(['$snapshot', '$autocapture', '$rageclick', '$dead_click']);

export const beforePaymentTelemetrySend: BeforeSendFn = (event) => {
  if (!event) return null;
  const redacted = attributeCarryingEvents.has(event.event)
    ? redactSnapshotTelemetry(event)
    : redactPaymentTelemetry(event);
  return redacted as typeof event;
};
